import { siteText } from '../siteText'
import { memo, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { TranscriptSegment } from '../types'
import { timedTranscriptParts, transcriptSpeaker } from '../transcriptInteraction'
import { formatDuration } from './ui'

export const TranscriptLine = memo(function TranscriptLine({ segment, time, canSeek, onSeek, onRenameSpeaker, draft = false }: {
  segment: TranscriptSegment; time: number; canSeek: boolean; onSeek: (seconds: number) => void; draft?: boolean; onRenameSpeaker?: (speakerId: string, name: string) => Promise<void>
}) {
  const parts = useMemo(() => timedTranscriptParts(segment), [segment])
  const speaker = transcriptSpeaker(segment)
  const exact = parts.some((part) => part.start !== undefined)
  const partial = exact && parts.some((part) => part.start === undefined && part.text.trim())
  const lineStart = exact && !partial ? parts.find((part) => part.start !== undefined)!.start! : segment.start
  const active = time >= segment.start && time < segment.end
  return <div className={`home-transcript-paragraph ${active ? 'is-current' : ''}`}>
    <button type="button" className="home-transcript-time" aria-label={siteText(`Play line at ${formatDuration(lineStart)}`)} disabled={!canSeek} onClick={() => onSeek(lineStart)}>{siteText(formatDuration(lineStart))}</button>
    <div className="transcript-line-content">
      <div className="transcript-speaker" title={siteText(speaker.description)}><span className="transcript-speaker-dot" aria-hidden="true" />{siteText(onRenameSpeaker && segment.speaker ? <SpeakerName key={segment.speaker} label={speaker.label} onSave={(name) => onRenameSpeaker(segment.speaker!, name)} /> : speaker.label)}{speaker.section !== null ? <span className="transcript-speaker-section">Section {speaker.section}</span> : null}</div>
      <p className="transcript-line-text" onClick={canSeek ? () => onSeek(lineStart) : undefined}>
        {!exact && canSeek ? <button type="button" className="transcript-line-play" aria-label={siteText(`Play line: ${segment.text}`)} onClick={(event) => { event.stopPropagation(); onSeek(lineStart) }}>{siteText(segment.text)}</button> : parts.map((part, index) => part.start !== undefined && canSeek
          ? <button type="button" key={index} className={`transcript-word ${time >= part.start && time < part.end! ? 'is-spoken' : ''}`} aria-label={siteText(`Play “${part.text}” at ${part.start.toFixed(2)} seconds`)} data-start={part.start} onClick={(event) => { event.stopPropagation(); onSeek(part.start!) }}>{siteText(part.text)}</button>
          : <span key={index}>{siteText(part.text)}</span>)}
        {draft ? <span className="transcript-draft-cursor" aria-hidden="true">▍</span> : null}
      </p>

    </div>
  </div>
})


function SpeakerName({ label, onSave }: { label: string; onSave: (name: string) => Promise<void> }) {
  const [draft, setDraft] = useState(label)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const focused = useRef(false)
  const skipBlur = useRef(false)
  const pending = useRef(false)
  const errorId = useId()
  useEffect(() => { if (!focused.current && !pending.current) setDraft(label) }, [label, saving])
  async function save() {
    focused.current = false
    if (skipBlur.current) { skipBlur.current = false; return }
    const name = draft.trim()
    if (name === label || pending.current) return
    pending.current = true; setSaving(true); setError(null)
    try { await onSave(name); setDraft(name || label) }
    catch { setError('Could not save. Try again.'); focused.current = true }
    finally { pending.current = false; setSaving(false) }
  }
  return <span className="speaker-name-field">
    <input className="speaker-name-input" aria-label={siteText(`Speaker name: ${label}`)} title="Rename this speaker throughout this section. Enter or click away to save; Escape to cancel." value={draft} maxLength={80} disabled={saving} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined}
      onFocus={() => { focused.current = true }} onChange={(event) => { setDraft(event.target.value); setError(null) }} onBlur={() => void save()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur() }
        if (event.key === 'Escape') { event.preventDefault(); skipBlur.current = true; setDraft(label); setError(null); event.currentTarget.blur() }
      }} />
    {saving ? <span className="sr-only" role="status">Saving speaker name</span> : null}
    {error ? <small id={errorId} className="speaker-name-error" role="alert">{siteText(error)}</small> : null}
  </span>
}
